import { useNavigate, useParams } from "react-router";
import { useSelector, useDispatch } from "react-redux";

import { WizardButtonsConfig } from "views/uikit/VLWizard";
import { useMemo, useCallback } from "react";
import useCreateDataset from "hooks/useCreateDataset";
import {
  getCreationStatus,
  getCreationStatusPolling,
  getFolderMetadata,
  getIsNewDatasetReady,
} from "redux/CreateDataset/selectors";
import { processDataset } from "redux/CreateDataset/actions";

interface UseDatasetWizardNavigation {
  currentStep: number;
  selectedFolderIds: string[];
  isFolderUploading: boolean;
  setCurrentStep: (step: number) => void;
}

const useDatasetWizardNavigation = ({
  currentStep,
  selectedFolderIds,
  isFolderUploading,
  setCurrentStep,
}: UseDatasetWizardNavigation): WizardButtonsConfig => {
  const { selectFolders } = useCreateDataset();
  const { newDatasetId } = useParams();
  const dispatch = useDispatch();
  const folderMetadata = useSelector(getFolderMetadata);
  const creationStatus = useSelector(getCreationStatus);
  const isNewDatasetReady = useSelector(getIsNewDatasetReady);
  const polling = useSelector(getCreationStatusPolling);
  const navigate = useNavigate();

  // Action functions
  const handleGoToPreview = useCallback(async () => {
    const isSuccess = await selectFolders(selectedFolderIds);
    if (!isSuccess) return;

    setCurrentStep(1);
  }, [selectFolders, selectedFolderIds, setCurrentStep]);

  const handleGoToPrevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, setCurrentStep]);

  const handleCreateDataset = useCallback(async () => {
    if (!newDatasetId) return;

    try {
      // Call the /process endpoint
      const success = await dispatch(processDataset(newDatasetId) as any);

      if (success) {
        // Navigate to CreateDataset step (step 2) - polling will be handled automatically
        setCurrentStep(2);
      }
    } catch (error) {
      console.error("Failed to process dataset:", error);
    }
  }, [dispatch, newDatasetId, setCurrentStep]);

  const handleFinish = useCallback(() => {
    navigate(`/dataset/${newDatasetId}/data`);
  }, [navigate, newDatasetId]);

  const allSelectedAreReady = useMemo(() => {
    const readyFolderIds = folderMetadata
      .filter((f) => f.status === "valid")
      .map((f) => f.id);

    return selectedFolderIds.every((id) => readyFolderIds.includes(id));
  }, [selectedFolderIds, folderMetadata]);

  const disabledShowPreviewText = useMemo(() => {
    const reasons = [
      {
        condition: folderMetadata.length === 0,
        message: "please upload at least one folder",
      },
      {
        condition: selectedFolderIds.length === 0,
        message: "please select at least one folder",
      },
      {
        condition: isFolderUploading && !allSelectedAreReady,
        message: "please wait for the folder to be uploaded",
      },
      {
        condition: !allSelectedAreReady,
        message: "please wait for selected folders to be ready",
      },
    ];

    return reasons.find((r) => r.condition)?.message || "";
  }, [
    folderMetadata,
    selectedFolderIds,
    isFolderUploading,
    allSelectedAreReady,
  ]);

  const disabledFinishText = useMemo(() => {
    if (isNewDatasetReady) {
      return "";
    }

    if (polling) {
      return "Please wait for the dataset to be created.";
    }

    if (creationStatus?.primaryStatus === "FATAL_ERROR") {
      return creationStatus.fatalError || "Dataset creation failed.";
    }

    return "";
  }, [creationStatus, polling, isNewDatasetReady]);

  return useMemo(() => {
    const stepConfigs: Record<number, WizardButtonsConfig> = {
      0: {
        // Add Data step
        showNext: true,
        showPrev: false,
        disableNextTooltip: disabledShowPreviewText,
        disablePrevTooltip: "no previous step",
        nextText: "Continue",
        prevText: "Previous",
        onNext: handleGoToPreview,
      },
      1: {
        // Preview step
        showNext: true,
        showPrev: true,
        disableNextTooltip: "",
        disablePrevTooltip: "",
        nextText: "Create Dataset",
        prevText: "Back",
        onNext: handleCreateDataset,
        onPrev: handleGoToPrevStep,
      },
      2: {
        // Create Dataset step
        showNext: true,
        showPrev: false,
        disableNextTooltip: disabledFinishText,
        disablePrevTooltip: "",
        nextText: "Explore Dataset",
        prevText: "Previous",
        onNext: handleFinish,
      },
    };

    return stepConfigs[currentStep];
  }, [
    currentStep,
    disabledFinishText,
    disabledShowPreviewText,
    handleGoToPreview,
    handleGoToPrevStep,
    handleCreateDataset,
    handleFinish,
  ]);
};

export default useDatasetWizardNavigation;
